import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  ChevronDown,
  Code2,
  Eraser,
  Image as ImageIcon,
  IndentDecrease,
  IndentIncrease,
  Italic,
  Link2,
  List,
  ListOrdered,
  Maximize2,
  Mic,
  Minimize2,
  Minus,
  MoreHorizontal,
  Paperclip,
  Palette,
  Strikethrough,
  Table2,
  Type,
  Underline,
  Video,
  Wand2,
} from "lucide-react";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@/design-system/native";
import styles from "./TextareaComposer.module.scss";
// Saf sabitler + yardımcılar ayrı dosyada (god-module split, SOLID).
import {
  buildFileItem,
  buildAudioItem,
  STYLE_OPTIONS,
  FONT_OPTIONS,
  TABLE_GRID_SIZE,
  LINE_HEIGHT_OPTIONS,
  COLOR_PALETTE,
  BLOCK_SELECTOR,
  defaultLinkForm,
  escapeHtml,
  escapeAttr,
  toRgba,
  normalizeUrl,
  normalizeHtmlForCodeView,
} from "./TextareaComposer.helpers";

const TextareaComposer = ({
  value,
  onChange,
  onSubmit,
  placeholder = "Write a comment...",
  submitLabel = "Post Comment",
  showToolbar = true,
  showToolbarOnFocus = false,
  resizable = false,
  allowFiles = true,
  allowAudio = true,
  autoFocus = false,
  disabled = false,
  className = "",
  hideActions = false,
  editorMinHeight = 200,
}) => {
  const [internalValue, setInternalValue] = useState(value ?? "");
  const [files, setFiles] = useState([]);
  const [audioClips, setAudioClips] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [styleOpen, setStyleOpen] = useState(false);
  const [fontOpen, setFontOpen] = useState(false);
  const [tableOpen, setTableOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [lineHeightOpen, setLineHeightOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);

  const [activeStyle, setActiveStyle] = useState("p");
  const [activeFont, setActiveFont] = useState("Rubik");
  const [activeLineHeight, setActiveLineHeight] = useState("1.0");
  const [tableHover, setTableHover] = useState({ rows: 0, cols: 0 });
  const [isCodeView, setIsCodeView] = useState(false);
  const [codeValue, setCodeValue] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [toolbarActivated, setToolbarActivated] = useState(!showToolbarOnFocus);

  const [colorMode, setColorMode] = useState("background");
  const [colorOpacity, setColorOpacity] = useState(1);
  const [activeTextColor, setActiveTextColor] = useState("#000000");
  const [activeBackgroundColor, setActiveBackgroundColor] = useState("#ffff00");

  const [linkForm, setLinkForm] = useState(defaultLinkForm);

  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const editorRef = useRef(null);
  const selectionRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const filesRef = useRef([]);
  const audioClipsRef = useRef([]);
  const inlineMediaUrlsRef = useRef([]);

  const textValue = value !== undefined ? value : internalValue;

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  useEffect(() => {
    setToolbarActivated(!showToolbarOnFocus);
  }, [showToolbarOnFocus]);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    audioClipsRef.current = audioClips;
  }, [audioClips]);

  useEffect(() => {
    try {
      document.execCommand("styleWithCSS", false, true);
    } catch (error) {
      /* no-op */
    }
  }, []);

  useEffect(() => {
    if (isCodeView) {
      setCodeValue(textValue || "");
      return;
    }
    if (editorRef.current && editorRef.current.innerHTML !== (textValue || "")) {
      editorRef.current.innerHTML = textValue || "";
    }
  }, [textValue, isCodeView]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      filesRef.current.forEach((item) => {
        if (item?.url) URL.revokeObjectURL(item.url);
      });
      audioClipsRef.current.forEach((item) => {
        if (item?.url) URL.revokeObjectURL(item.url);
      });
      inlineMediaUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    if (!isFullScreen || typeof document === "undefined") return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isFullScreen]);

  const handleTextChange = (next) => {
    if (value === undefined) {
      setInternalValue(next);
    }
    onChange?.(next);
  };

  const saveSelection = () => {
    if (!editorRef.current || isCodeView) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!editorRef.current.contains(range.commonAncestorContainer)) return;
    selectionRef.current = range.cloneRange();
  };

  const restoreSelection = () => {
    if (!editorRef.current || isCodeView) return;
    const selection = window.getSelection();
    if (!selection || !selectionRef.current) return;
    if (!editorRef.current.contains(selectionRef.current.commonAncestorContainer)) {
      return;
    }
    selection.removeAllRanges();
    selection.addRange(selectionRef.current);
  };

  const focusEditor = () => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  const revealToolbar = () => {
    if (showToolbarOnFocus && !toolbarActivated) {
      setToolbarActivated(true);
    }
  };

  const exec = (command, valueArg) => {
    if (disabled || isCodeView) return;
    if (!editorRef.current) return;
    focusEditor();
    restoreSelection();
    document.execCommand(command, false, valueArg);
    saveSelection();
    handleTextChange(editorRef.current?.innerHTML || "");
  };

  const applyStyle = (option) => {
    if (!option) return;
    setActiveStyle(option.label);
    exec("formatBlock", `<${option.block}>`);
    setStyleOpen(false);
  };

  const applyFont = (font) => {
    if (!font) return;
    setActiveFont(font);
    exec("fontName", font);
    setFontOpen(false);
  };

  const insertTable = (rows, cols) => {
    if (!rows || !cols) return;
    const cells = "<td><br/></td>".repeat(cols);
    const rowsHtml = Array.from({ length: rows })
      .map(() => `<tr>${cells}</tr>`)
      .join("");
    const html = `<table class="comment-table"><tbody>${rowsHtml}</tbody></table><p><br/></p>`;
    exec("insertHTML", html);
  };

  const applyInlineStyle = (styles) => {
    if (disabled || isCodeView || !editorRef.current) return;
    focusEditor();
    restoreSelection();

    const styleEntries = Object.entries(styles)
      .filter(([, styleValue]) => styleValue !== undefined && styleValue !== null)
      .map(([key, styleValue]) => [key, String(styleValue)]);

    if (styleEntries.length === 0) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!editorRef.current.contains(range.commonAncestorContainer)) return;
    const span = document.createElement("span");
    styleEntries.forEach(([key, styleValue]) => {
      span.style.setProperty(key, styleValue);
    });

    if (range.collapsed) {
      const textNode = document.createTextNode("\u200B");
      span.appendChild(textNode);
      range.insertNode(span);

      const nextRange = document.createRange();
      nextRange.setStart(textNode, 1);
      nextRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(nextRange);
      selectionRef.current = nextRange.cloneRange();
      handleTextChange(editorRef.current.innerHTML || "");
      return;
    }

    const fragment = range.extractContents();
    span.appendChild(fragment);
    range.insertNode(span);

    const nextRange = document.createRange();
    nextRange.selectNodeContents(span);
    nextRange.collapse(false);
    selection.removeAllRanges();
    selection.addRange(nextRange);
    selectionRef.current = nextRange.cloneRange();
    handleTextChange(editorRef.current.innerHTML || "");
  };

  const applyColor = (hexColor) => {
    if (!hexColor) return;
    const rgba = toRgba(hexColor, colorOpacity);

    if (colorMode === "text") {
      setActiveTextColor(hexColor);
      applyInlineStyle({ color: rgba });
      setColorOpen(false);
      return;
    }

    setActiveBackgroundColor(hexColor);
    applyInlineStyle({ "background-color": rgba });
    setColorOpen(false);
  };

  const clearColorStyles = () => {
    setActiveTextColor("#000000");
    setActiveBackgroundColor("#ffff00");
    applyInlineStyle({ color: "inherit", "background-color": "transparent" });
    setColorOpen(false);
  };

  const resolveClosestBlock = (node) => {
    let currentNode = node;
    if (!currentNode) return null;
    if (currentNode.nodeType === Node.TEXT_NODE) {
      currentNode = currentNode.parentElement;
    }

    while (currentNode && currentNode !== editorRef.current) {
      if (currentNode.matches?.(BLOCK_SELECTOR)) {
        return currentNode;
      }
      currentNode = currentNode.parentElement;
    }
    return null;
  };

  const collectSelectedBlocks = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editorRef.current) return [];

    const range = selection.getRangeAt(0);
    if (!editorRef.current.contains(range.commonAncestorContainer)) return [];

    if (range.collapsed) {
      const currentBlock = resolveClosestBlock(selection.anchorNode);
      return currentBlock ? [currentBlock] : [];
    }

    const found = new Set();
    const walker = document.createTreeWalker(editorRef.current, NodeFilter.SHOW_ELEMENT);

    while (walker.nextNode()) {
      const element = walker.currentNode;
      if (!element.matches?.(BLOCK_SELECTOR)) continue;
      if (range.intersectsNode(element)) {
        found.add(element);
      }
    }

    return Array.from(found);
  };

  const applyLineHeight = (lineHeight) => {
    if (disabled || isCodeView || !editorRef.current) return;
    focusEditor();
    restoreSelection();

    const targets = collectSelectedBlocks();
    const destination = targets.length > 0 ? targets : [editorRef.current];

    destination.forEach((item) => {
      item.style.lineHeight = lineHeight;
    });

    setActiveLineHeight(lineHeight);
    setLineHeightOpen(false);
    handleTextChange(editorRef.current.innerHTML || "");
    saveSelection();
  };

  const runAdvancedCommand = (command) => {
    exec(command);
    setAdvancedOpen(false);
  };

  const openLinkModal = () => {
    if (disabled || isCodeView) return;
    focusEditor();
    saveSelection();

    const selected = window.getSelection()?.toString()?.trim() || "";
    setLinkForm((prev) => ({
      ...prev,
      text: selected,
      url: "",
    }));
    setLinkModalOpen(true);
  };

  const handleInsertLink = () => {
    const normalizedUrl = normalizeUrl(linkForm.url, linkForm.useDefaultProtocol);
    if (!normalizedUrl) return;

    const displayText = (linkForm.text || normalizedUrl).trim();
    const targetHtml = linkForm.openInNewWindow
      ? ' target="_blank" rel="noopener noreferrer"'
      : "";

    exec(
      "insertHTML",
      `<a href="${escapeAttr(normalizedUrl)}"${targetHtml}>${escapeHtml(
        displayText
      )}</a>`
    );

    setLinkModalOpen(false);
    setLinkForm(defaultLinkForm);
  };

  const insertInlineMedia = (event, mediaType) => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length) return;

    const html = selected
      .map((file) => {
        const url = URL.createObjectURL(file);
        inlineMediaUrlsRef.current.push(url);

        if (mediaType === "image") {
          return `<img class="comment-inline-image" src="${escapeAttr(url)}" alt="${escapeAttr(
            file.name
          )}" />`;
        }

        return `<video class="comment-inline-video" controls src="${escapeAttr(
          url
        )}"></video>`;
      })
      .join("<p><br/></p>");

    exec("insertHTML", `${html}<p><br/></p>`);
    event.target.value = "";
  };

  const toggleCodeView = () => {
    if (isCodeView) {
      setIsCodeView(false);
      if (editorRef.current) {
        editorRef.current.innerHTML = codeValue;
      }
      handleTextChange(codeValue);
      return;
    }

    const html = editorRef.current?.innerHTML || textValue || "";
    const normalized = normalizeHtmlForCodeView(html);
    setCodeValue(normalized);
    setIsCodeView(true);
  };

  const handleEditorInput = () => {
    const html = editorRef.current?.innerHTML || "";
    handleTextChange(html);
  };

  const handleFilesSelected = (event) => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length) return;
    const mapped = selected.map(buildFileItem);
    setFiles((prev) => [...prev, ...mapped]);
    event.target.value = "";
  };

  const removeFile = (id) => {
    setFiles((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target?.url) URL.revokeObjectURL(target.url);
      return prev.filter((item) => item.id !== id);
    });
  };

  const removeAudio = (id) => {
    setAudioClips((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target?.url) URL.revokeObjectURL(target.url);
      return prev.filter((item) => item.id !== id);
    });
  };

  const clearAttachments = () => {
    setFiles((prev) => {
      prev.forEach((item) => item?.url && URL.revokeObjectURL(item.url));
      return [];
    });
    setAudioClips((prev) => {
      prev.forEach((item) => item?.url && URL.revokeObjectURL(item.url));
      return [];
    });
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRecordingSeconds(0);
    setIsRecording(false);
  };

  const startRecording = async () => {
    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };

      recorder.onstop = () => {
        if (chunks.length > 0) {
          const blob = new Blob(chunks, { type: recorder.mimeType });
          setAudioClips((prev) => [buildAudioItem(blob), ...prev]);
        }

        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        recorderRef.current = null;
      };

      streamRef.current = stream;
      recorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      timerRef.current = setInterval(() => {
        setRecordingSeconds((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Audio recording error:", error);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleSubmit = () => {
    const source = isCodeView ? codeValue : textValue;
    const html = (source || "").trim();
    const plain = html.replace(/<[^>]*>/g, "").trim();
    const hasMedia = /<(table|hr|img|audio|video)/i.test(html);
    if (!plain && !hasMedia && files.length === 0 && audioClips.length === 0) {
      return;
    }
    onSubmit?.({
      text: html,
      files,
      audios: audioClips,
    });
    handleTextChange("");
    setCodeValue("");
    clearAttachments();
    setIsRecording(false);
  };

  const attachments = useMemo(() => files, [files]);
  const audioItems = useMemo(() => audioClips, [audioClips]);
  const toolbarDisabled = disabled || isCodeView;
  const shouldRenderToolbar = showToolbar && (!showToolbarOnFocus || toolbarActivated);
  const editorShellClassName = `comment-editor-shell ${
    resizable ? "comment-editor-shell--resizable" : ""
  } ${styles.minHeight}`;
  const editorShellMinHeight = editorMinHeight != null ? `${editorMinHeight}px` : undefined;

  return (
    <>
      <div
        className={`comment-composer ${isFullScreen ? "comment-composer--fullscreen" : ""} ${
          className || ""
        }`}
      >
        {shouldRenderToolbar && (
          <div className="comment-toolbar">
            <Dropdown
              isOpen={styleOpen}
              toggle={() => {
                if (!toolbarDisabled) setStyleOpen((current) => !current);
              }}
            >
              <DropdownToggle
                color="light"
                className="cc-toolbar-select"
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                disabled={toolbarDisabled}
              >
                <Wand2 size={14} className={styles.iconLeading} />
                {activeStyle}
                <ChevronDown size={14} className={styles.iconTrailing} />
              </DropdownToggle>
              <DropdownMenu className="cc-toolbar-menu">
                {STYLE_OPTIONS.map((option) => (
                  <DropdownItem
                    key={option.label}
                    toggle={false}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => applyStyle(option)}
                    style={option.style}
                  >
                    {option.label}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>

            <div className="cc-toolbar-segment">
              <button
                type="button"
                className="cc-toolbar-btn"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => exec("bold")}
                disabled={toolbarDisabled}
              >
                <Bold size={14} />
              </button>
              <button
                type="button"
                className="cc-toolbar-btn"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => exec("italic")}
                disabled={toolbarDisabled}
              >
                <Italic size={14} />
              </button>
              <button
                type="button"
                className="cc-toolbar-btn"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => exec("underline")}
                disabled={toolbarDisabled}
              >
                <Underline size={14} />
              </button>
              <button
                type="button"
                className="cc-toolbar-btn"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => exec("strikeThrough")}
                disabled={toolbarDisabled}
              >
                <Strikethrough size={14} />
              </button>
            </div>

            <Dropdown
              isOpen={fontOpen}
              toggle={() => {
                if (!toolbarDisabled) setFontOpen((current) => !current);
              }}
            >
              <DropdownToggle
                color="light"
                className="cc-toolbar-select"
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                disabled={toolbarDisabled}
              >
                {activeFont}
                <ChevronDown size={14} className={styles.iconTrailing} />
              </DropdownToggle>
              <DropdownMenu className="cc-toolbar-menu">
                {FONT_OPTIONS.map((font) => (
                  <DropdownItem
                    key={font}
                    toggle={false}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => applyFont(font)}
                    className={styles.fontOption}
                    style={{ "--font-family": font }}
                  >
                    {font}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>

            <Dropdown
              isOpen={colorOpen}
              toggle={() => {
                if (toolbarDisabled) return;
                if (!colorOpen) saveSelection();
                setColorOpen((current) => !current);
              }}
            >
              <DropdownToggle
                color="light"
                className="cc-toolbar-select cc-color-toggle"
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                disabled={toolbarDisabled}
              >
                <span className="cc-color-letter">A</span>
                <Palette size={13} />
                <ChevronDown size={14} />
              </DropdownToggle>

              <DropdownMenu className="cc-color-menu">
                <div className="cc-color-head">
                  <button
                    type="button"
                    className={`cc-color-mode ${colorMode === "background" ? "active" : ""}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => setColorMode("background")}
                  >
                    Arka plan rengi
                  </button>
                  <button
                    type="button"
                    className={`cc-color-mode ${colorMode === "text" ? "active" : ""}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => setColorMode("text")}
                  >
                    Yazı rengi
                  </button>
                </div>

                <div className="cc-opacity-line">
                  <span>Şeffaflık</span>
                  <span>{Math.round(colorOpacity * 100)}%</span>
                </div>
                <Input
                  type="range"
                  min="0"
                  max="100"
                  value={Math.round(colorOpacity * 100)}
                  onChange={(event) => setColorOpacity(Number(event.target.value) / 100)}
                />

                <button
                  type="button"
                  className="cc-color-reset"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={clearColorStyles}
                >
                  Varsayılanlara sıfırla
                </button>

                <div className="cc-color-palette">
                  {COLOR_PALETTE.map((item) => {
                    const isActive =
                      colorMode === "text" ? activeTextColor === item : activeBackgroundColor === item;

                    return (
                      <button
                        key={item}
                        type="button"
                        className={`cc-color-cell ${isActive ? "is-active" : ""} ${styles.colorCell}`}
                        style={{ "--bg": item }}
                        title={item}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => applyColor(item)}
                      />
                    );
                  })}
                </div>
              </DropdownMenu>
            </Dropdown>

            <div className="cc-toolbar-segment">
              <button
                type="button"
                className="cc-toolbar-btn"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => exec("insertUnorderedList")}
                disabled={toolbarDisabled}
              >
                <List size={14} />
              </button>
              <button
                type="button"
                className="cc-toolbar-btn"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => exec("insertOrderedList")}
                disabled={toolbarDisabled}
              >
                <ListOrdered size={14} />
              </button>
              <button
                type="button"
                className="cc-toolbar-btn"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => exec("justifyLeft")}
                disabled={toolbarDisabled}
              >
                <AlignLeft size={14} />
              </button>

              <Dropdown
                isOpen={advancedOpen}
                toggle={() => {
                  if (!toolbarDisabled) setAdvancedOpen((current) => !current);
                }}
              >
                <DropdownToggle
                  color="light"
                  className="cc-toolbar-btn cc-toolbar-btn--dropdown"
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  disabled={toolbarDisabled}
                >
                  <MoreHorizontal size={14} />
                </DropdownToggle>
                <DropdownMenu className="cc-toolbar-menu" end>
                  <DropdownItem
                    toggle={false}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => runAdvancedCommand("justifyCenter")}
                  >
                    <AlignCenter size={14} className={styles.itemIcon} />
                    Ortala
                  </DropdownItem>
                  <DropdownItem
                    toggle={false}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => runAdvancedCommand("justifyRight")}
                  >
                    <AlignRight size={14} className={styles.itemIcon} />
                    Sağa hizala
                  </DropdownItem>
                  <DropdownItem
                    toggle={false}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => runAdvancedCommand("outdent")}
                  >
                    <IndentDecrease size={14} className={styles.itemIcon} />
                    Girintiyi azalt
                  </DropdownItem>
                  <DropdownItem
                    toggle={false}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => runAdvancedCommand("indent")}
                  >
                    <IndentIncrease size={14} className={styles.itemIcon} />
                    Girinti artır
                  </DropdownItem>
                  <DropdownItem
                    toggle={false}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => runAdvancedCommand("removeFormat")}
                  >
                    <Eraser size={14} className={styles.itemIcon} />
                    Biçimi temizle
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>

            <Dropdown
              isOpen={lineHeightOpen}
              toggle={() => {
                if (!toolbarDisabled) setLineHeightOpen((current) => !current);
              }}
            >
              <DropdownToggle
                color="light"
                className="cc-toolbar-select"
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                disabled={toolbarDisabled}
              >
                <Type size={14} className={styles.iconLeading} />
                {activeLineHeight}
                <ChevronDown size={14} className={styles.iconTrailing} />
              </DropdownToggle>
              <DropdownMenu className="cc-toolbar-menu">
                {LINE_HEIGHT_OPTIONS.map((item) => (
                  <DropdownItem
                    key={item}
                    toggle={false}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => applyLineHeight(item)}
                  >
                    {item}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>

            <div className="cc-table-wrapper">
              <button
                type="button"
                className="cc-toolbar-btn"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setTableOpen((current) => !current)}
                disabled={toolbarDisabled}
              >
                <Table2 size={14} />
              </button>
              {tableOpen && (
                <div className="cc-table-popover" onMouseDown={(event) => event.preventDefault()}>
                  <div className="cc-table-label">
                    {tableHover.rows} x {tableHover.cols}
                  </div>
                  <div className="cc-table-grid">
                    {Array.from({ length: TABLE_GRID_SIZE }).map((_, rowIdx) =>
                      Array.from({ length: TABLE_GRID_SIZE }).map((__, colIdx) => {
                        const rows = rowIdx + 1;
                        const cols = colIdx + 1;
                        const active = rows <= tableHover.rows && cols <= tableHover.cols;
                        return (
                          <div
                            key={`${rowIdx}-${colIdx}`}
                            className={`cc-table-cell ${active ? "is-active" : ""}`}
                            onMouseEnter={() => setTableHover({ rows, cols })}
                            onClick={() => {
                              insertTable(rows, cols);
                              setTableOpen(false);
                              setTableHover({ rows: 0, cols: 0 });
                            }}
                          />
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              className="cc-toolbar-btn"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => exec("insertHorizontalRule")}
              disabled={toolbarDisabled}
            >
              <Minus size={14} />
            </button>

            <button
              type="button"
              className="cc-toolbar-btn"
              onMouseDown={(event) => event.preventDefault()}
              onClick={openLinkModal}
              disabled={toolbarDisabled}
            >
              <Link2 size={14} />
            </button>

            <button
              type="button"
              className="cc-toolbar-btn"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => imageInputRef.current?.click()}
              disabled={toolbarDisabled}
            >
              <ImageIcon size={14} />
            </button>

            <button
              type="button"
              className="cc-toolbar-btn"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => videoInputRef.current?.click()}
              disabled={toolbarDisabled}
            >
              <Video size={14} />
            </button>

            <button
              type="button"
              className="cc-toolbar-btn"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => setIsFullScreen((current) => !current)}
            >
              {isFullScreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
            <button
              type="button"
              className="cc-toolbar-btn"
              onMouseDown={(event) => event.preventDefault()}
              onClick={toggleCodeView}
            >
              <Code2 size={14} />
            </button>

            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              multiple
              className={styles.visuallyHiddenInput}
              onChange={(event) => insertInlineMedia(event, "image")}
            />
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              multiple
              className={styles.visuallyHiddenInput}
              onChange={(event) => insertInlineMedia(event, "video")}
            />
          </div>
        )}

        <div className={editorShellClassName} style={{ "--min-h": editorShellMinHeight }}>
          {isCodeView ? (
            <textarea
              className={`comment-codearea ${styles.minHeight}`}
              style={{ "--min-h": editorShellMinHeight }}
              value={codeValue}
              onFocus={revealToolbar}
              onChange={(event) => {
                setCodeValue(event.target.value);
                handleTextChange(event.target.value);
              }}
              autoFocus={autoFocus}
              disabled={disabled}
            />
          ) : (
            <div
              ref={editorRef}
              className={`comment-editor ${styles.minHeight}`}
              style={{ "--min-h": editorShellMinHeight }}
              contentEditable={!disabled}
              data-placeholder={placeholder}
              onFocus={revealToolbar}
              onInput={handleEditorInput}
              onKeyUp={saveSelection}
              onMouseUp={saveSelection}
              onBlur={saveSelection}
              suppressContentEditableWarning
              spellCheck
            />
          )}
        </div>

        {(attachments.length > 0 || audioItems.length > 0) && (
          <div className="comment-attachments">
            {attachments.map((item) => {
              const isImage = item.type?.startsWith("image/");
              return (
                <div key={item.id} className="comment-attachment">
                  {isImage ? (
                    <img src={item.url} alt={item.name} />
                  ) : (
                    <div className="comment-file-name">{item.name}</div>
                  )}
                  <button
                    type="button"
                    className="comment-attachment-remove"
                    onClick={() => removeFile(item.id)}
                  >
                    ×
                  </button>
                </div>
              );
            })}
            {audioItems.map((item) => (
              <div key={item.id} className="comment-audio">
                <audio controls src={item.url} />
                <button
                  type="button"
                  className="comment-attachment-remove"
                  onClick={() => removeAudio(item.id)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {!hideActions && (
          <div className="comment-actions">
          <div className={styles.actionsCluster}>
            {allowFiles && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className={styles.visuallyHiddenInput}
                  onChange={handleFilesSelected}
                />
                <Button
                  type="button"
                  color="light"
                  className="comment-action-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled}
                >
                  <Paperclip size={14} className={styles.iconLeading} />
                  Upload File
                </Button>
              </>
            )}
            {allowAudio && (
              <Button
                type="button"
                color={isRecording ? "danger" : "light"}
                className={`comment-action-btn comment-audio-btn ${
                  isRecording ? "is-recording" : ""
                }`}
                onClick={toggleRecording}
                disabled={disabled}
              >
                <Mic size={14} />
                {isRecording && (
                  <span className={styles.iconTrailing}>
                    {recordingSeconds}
                    s
                  </span>
                )}
              </Button>
            )}
          </div>
          <Button
            type="button"
            color="primary"
            className="comment-submit-btn"
            onClick={handleSubmit}
            disabled={disabled}
          >
            {submitLabel}
          </Button>
          </div>
        )}
      </div>

      <Modal isOpen={linkModalOpen} toggle={() => setLinkModalOpen(false)} className="cc-link-modal" centered>
        <ModalHeader toggle={() => setLinkModalOpen(false)}>Bağlantı ekle</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="cc-link-text">Görüntülemek için</Label>
            <Input
              id="cc-link-text"
              value={linkForm.text}
              onChange={(event) =>
                setLinkForm((current) => ({
                  ...current,
                  text: event.target.value,
                }))
              }
            />
          </FormGroup>
          <FormGroup>
            <Label for="cc-link-url">Bağlantı adresi?</Label>
            <Input
              id="cc-link-url"
              value={linkForm.url}
              onChange={(event) =>
                setLinkForm((current) => ({
                  ...current,
                  url: event.target.value,
                }))
              }
            />
          </FormGroup>
          <FormGroup check className={styles.formGroupGap}>
            <Input
              id="cc-link-new-window"
              type="checkbox"
              checked={linkForm.openInNewWindow}
              onChange={(event) =>
                setLinkForm((current) => ({
                  ...current,
                  openInNewWindow: event.target.checked,
                }))
              }
            />
            <Label check for="cc-link-new-window">
              Yeni pencerede aç
            </Label>
          </FormGroup>
          <FormGroup check>
            <Input
              id="cc-link-protocol"
              type="checkbox"
              checked={linkForm.useDefaultProtocol}
              onChange={(event) =>
                setLinkForm((current) => ({
                  ...current,
                  useDefaultProtocol: event.target.checked,
                }))
              }
            />
            <Label check for="cc-link-protocol">
              Use default protocol
            </Label>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleInsertLink}>
            Bağlantı ekle
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default TextareaComposer;
